import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Paper, Stack, CircularProgress, Alert, Grid, Card,
  CardContent, CardActions, Avatar, Chip, Button, IconButton, Tooltip,
  alpha, Divider, Dialog, DialogTitle, DialogContent,
  TextField, useTheme, Stepper, Step, StepLabel, StepContent, Radio,
  RadioGroup, FormControlLabel
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import ImageIcon from '@mui/icons-material/Image';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import BrushIcon from '@mui/icons-material/Brush';
import { useAuth } from '../context/AuthContext';
import { fetchDesignPlans, fetchPlanWallets, purchasePlan } from '../designApi';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import DesignGallery from '../components/DesignGallery';

// ✅ Helper to safely extract an array from any API response structure
const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.plans)) return data.plans;
  if (Array.isArray(data.wallets)) return data.wallets;
  if (Array.isArray(data.subscriptions)) return data.subscriptions;
  if (Array.isArray(data.rows)) return data.rows;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.results)) return data.results;
  if (Array.isArray(data.items)) return data.items;
  return [];
};

// ✅ Helper to safely get nested field values with fallbacks
const getField = (obj, ...fields) => {
  if (!obj) return null;
  for (const field of fields) {
    const parts = field.split('.');
    let val = obj;
    for (const part of parts) {
      val = val?.[part];
      if (val === undefined || val === null) break;
    }
    if (val !== undefined && val !== null) return val;
  }
  return null;
};

// ✅ Helper to convert a File to base64 data URI string
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export default function PurchaseDesignPlan() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Purchase flow state
  const [purchaseDialog, setPurchaseDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planWallets, setPlanWallets] = useState([]);
  const [walletsLoading, setWalletsLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [notes, setNotes] = useState('');
  const [purchasing, setPurchasing] = useState(false);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await fetchDesignPlans(false);
      console.log('📦 fetchDesignPlans response:', data);

      const plansArray = extractArray(data);
      console.log('📦 Extracted plans:', plansArray.length, 'items');
      setPlans(plansArray);
    } catch (err) {
      console.error('Failed to load plans:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load design plans');
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const handleSelectPlan = async (plan) => {
    const planId = getField(plan, 'Id', 'id');

    setSelectedPlan(plan);
    setActiveStep(0);
    setSelectedWallet(null);
    setReceiptFile(null);
    setReceiptPreview('');
    setNotes('');
    setPurchaseDialog(true);

    if (!planId) {
      setError('Invalid plan selected');
      return;
    }

    try {
      setWalletsLoading(true);
      setError('');
      const data = await fetchPlanWallets(planId);
      console.log('📦 fetchPlanWallets response:', data);

      const walletsArray = extractArray(data);
      console.log('📦 Extracted wallets:', walletsArray.length, 'items');
      setPlanWallets(walletsArray);
    } catch (err) {
      console.error('Failed to load plan wallets:', err);
      setError('Failed to load payment wallets');
      setPlanWallets([]);
    } finally {
      setWalletsLoading(false);
    }
  };

  const handleReceiptFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (JPG, PNG, WEBP)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setReceiptFile(file);
      setError('');
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result); // ✅ This is already a base64 data URI
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePurchase = async () => {
    if (!selectedWallet) {
      setError('Please select a payment wallet');
      return;
    }
    if (!receiptFile && !receiptPreview) {
      setError('Please upload a payment receipt');
      return;
    }

    try {
      setPurchasing(true);
      setError('');

      const planId = getField(selectedPlan, 'Id', 'id');
      const userId = getField(user, 'Id', 'id', 'userId', 'UserId');
      const walletId = getField(selectedWallet, 'WalletId', 'walletId', 'Id', 'id');
      const walletNumber = getField(selectedWallet, 'WalletNumber', 'walletNumber') || '';

      if (!planId) {
        setError('Invalid plan selected');
        setPurchasing(false);
        return;
      }
      if (!userId) {
        setError('User not authenticated');
        setPurchasing(false);
        return;
      }
      if (!walletId) {
        setError('Invalid wallet selected');
        setPurchasing(false);
        return;
      }
      if (!walletNumber) {
        setError('Wallet number is missing');
        setPurchasing(false);
        return;
      }

      // ✅ CRITICAL FIX: Backend expects base64 string, NOT FormData
      // Backend: const receipt = receiptImage || ReceiptImage;
      // Backend: const receiptPath = saveReceiptImage(receipt);
      // saveReceiptImage() parses "data:image/..." base64 strings
      let receiptBase64 = receiptPreview;
      if (receiptFile && !receiptBase64) {
        receiptBase64 = await fileToBase64(receiptFile);
      }

      if (!receiptBase64 || !receiptBase64.startsWith('data:image')) {
        setError('Invalid receipt image. Please re-upload.');
        setPurchasing(false);
        return;
      }

      // ✅ Build JSON body matching backend expectations exactly
      const requestBody = {
        adminId: parseInt(userId),
        planId: parseInt(planId),
        walletId: parseInt(walletId),
        walletNumber: walletNumber,
        receiptImage: receiptBase64
      };

      // Add notes if provided (backend doesn't use notes in purchasePlan,
      // but include for future compatibility)
      if (notes.trim()) {
        requestBody.notes = notes.trim();
      }

      console.log('🟡 Submitting purchase (JSON):', {
        adminId: requestBody.adminId,
        planId: requestBody.planId,
        walletId: requestBody.walletId,
        walletNumber: requestBody.walletNumber,
        receiptImageLength: receiptBase64.length,
        hasNotes: !!requestBody.notes
      });

      // ✅ Send as JSON, NOT FormData
      const result = await purchasePlan(requestBody);
      console.log('✅ Purchase result:', result);

      setSuccess(
        result?.message ||
        'Purchase request submitted successfully! You will be notified once approved.'
      );
      setPurchaseDialog(false);
      resetPurchaseForm();
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      console.error('❌ Purchase error:', err);
      console.error('❌ Error response:', err.response?.data);
      setError(err.response?.data?.message || err.message || 'Failed to submit purchase request');
    } finally {
      setPurchasing(false);
    }
  };

  const resetPurchaseForm = () => {
    setSelectedPlan(null);
    setSelectedWallet(null);
    setReceiptFile(null);
    setReceiptPreview('');
    setNotes('');
    setActiveStep(0);
    setPlanWallets([]);
  };

  const handleClosePurchaseDialog = () => {
    if (!purchasing) {
      setPurchaseDialog(false);
      resetPurchaseForm();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha('#ad1457', 0.08)} 0%, ${alpha('#6a1b9a', 0.08)} 100%)`,
          border: `1px solid ${alpha('#ad1457', 0.15)}`
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: '#ad1457', width: 50, height: 50 }}>
              <ShoppingCartIcon sx={{ fontSize: 28 }} />
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight={700} color="#ad1457">
                {t('PurchaseDesignPlan') || 'Purchase Design Plan'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('PurchasePlanDesc') || 'Choose a plan and subscribe to our design services'}
              </Typography>
            </Box>
          </Stack>
          <Tooltip title={t('Refresh') || 'Refresh'}>
            <IconButton onClick={loadPlans} sx={{ bgcolor: alpha('#ad1457', 0.1) }}>
              <RefreshIcon sx={{ color: '#ad1457' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* ─── Previous Work Gallery ─── */}
      <Box sx={{ mb: 4 }}>
        <DesignGallery />
      </Box>

            {/* ─── Plans Section Title ─── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{ pt: 2, pb: 4, px: 2 }}
      >
        <Box
          sx={{
            flex: 1,
            maxWidth: 100,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, transparent, ${alpha('#ad1457', 0.3)})`,
          }}
        />
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <AutoAwesomeIcon sx={{ color: '#ad1457', fontSize: 30 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: '#ad1457',
              letterSpacing: 0.5,
              fontSize: { xs: '1.4rem', sm: '1.7rem', md: '1.9rem' },
            }}
          >
            {t('AvailablePlans') || 'Available Plans'}
          </Typography>
          <AutoAwesomeIcon sx={{ color: '#ad1457', fontSize: 30 }} />
        </Stack>
        <Box
          sx={{
            flex: 1,
            maxWidth: 100,
            height: 2,
            borderRadius: 1,
            background: `linear-gradient(90deg, ${alpha('#ad1457', 0.3)}, transparent)`,
          }}
        />
      </Stack>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: `1px dashed ${alpha('#000', 0.2)}` }}>
          <BrushIcon sx={{ fontSize: 64, color: alpha('#000', 0.15), mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {t('NoPlansAvailable') || 'No design plans available at the moment'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('CheckBackLater') || 'Please check back later or contact support'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {plans.map((plan, index) => {
            const planId = getField(plan, 'Id', 'id');
            const planName = getField(plan, 'Name', 'name') || 'Unnamed Plan';
            const planDescription = getField(plan, 'Description', 'description');
            const planDesigns = getField(plan, 'NumberOfDesigns', 'numberOfDesigns') || 0;
            const planPrice = getField(plan, 'Price', 'price') || 0;
            const planCurrency = getField(plan, 'Currency', 'currency') || 'YER';
            const planFormattedPrice = getField(plan, 'FormattedPrice', 'formattedPrice');

            return (
              <Grid item xs={12} sm={6} md={4} key={planId || index}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha('#ad1457', 0.15)}`,
                    transition: 'all 0.3s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      boxShadow: `0 8px 30px ${alpha('#ad1457', 0.15)}`,
                      transform: 'translateY(-4px)',
                      borderColor: alpha('#ad1457', 0.3)
                    }
                  }}
                >
                  <Box
                    sx={{
                      p: 3,
                      textAlign: 'center',
                      background: `linear-gradient(135deg, ${alpha('#ad1457', 0.08)} 0%, ${alpha('#6a1b9a', 0.08)} 100%)`
                    }}
                  >
                    <Avatar sx={{ bgcolor: '#ad1457', width: 56, height: 56, mx: 'auto', mb: 1.5 }}>
                      <DesignServicesIcon sx={{ fontSize: 30 }} />
                    </Avatar>
                    <Typography variant="h6" fontWeight={700}>
                      {planName}
                    </Typography>
                    {planDescription && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {planDescription}
                      </Typography>
                    )}
                  </Box>

                  <CardContent sx={{ flex: 1, p: 3 }}>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: alpha('#9c27b0', 0.1), width: 36, height: 36 }}>
                          <ImageIcon sx={{ color: '#9c27b0', fontSize: 20 }} />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                             {t('Designs') || 'Designs'} : {planDesigns}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('ProfessionalDesigns') || 'Professional design images'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={800} color="#ad1457">
                        {planPrice || planFormattedPrice} {planCurrency} 
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {planFormattedPrice} 
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      size="large"
                      startIcon={<ShoppingCartIcon />}
                      onClick={() => handleSelectPlan(plan)}
                      sx={{
                        bgcolor: '#ad1457',
                        textTransform: 'none',
                        borderRadius: 2,
                        fontWeight: 700,
                        py: 1.5,
                        fontSize: '1rem',
                        '&:hover': { bgcolor: '#880e4f' }
                      }}
                    >
                      {t('PurchaseNow') || 'Purchase Now'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Purchase Dialog with Stepper */}
      <Dialog
        open={purchaseDialog}
        onClose={handleClosePurchaseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ShoppingCartIcon sx={{ color: '#ad1457' }} />
            <span>{t('PurchasePlan') || 'Purchase Plan'}</span>
          </Stack>
          <IconButton onClick={handleClosePurchaseDialog} disabled={purchasing}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedPlan && (() => {
            const spName = getField(selectedPlan, 'Name', 'name') || 'Plan';
            const spDesigns = getField(selectedPlan, 'NumberOfDesigns', 'numberOfDesigns') || 0;
            const spPrice = getField(selectedPlan, 'Price', 'price') || 0;
            const spCurrency = getField(selectedPlan, 'Currency', 'currency') || 'YER';
            const spFormattedPrice = getField(selectedPlan, 'FormattedPrice', 'formattedPrice');
            const displayPrice = spFormattedPrice || `${spPrice} ${spCurrency}`;

            return (
              <>
                {/* Selected Plan Summary */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: alpha('#ad1457', 0.04), borderRadius: 2, border: `1px solid ${alpha('#ad1457', 0.15)}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body1" fontWeight={700}>{spName}</Typography>
                      <Chip
                        icon={<ImageIcon sx={{ fontSize: 14 }} />}
                        label={`${spDesigns} designs`}
                        size="small"
                        sx={{ mt: 0.5, bgcolor: alpha('#9c27b0', 0.1), color: '#9c27b0' }}
                      />
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#ad1457">
                      {displayPrice}
                    </Typography>
                  </Stack>
                </Paper>

                {/* Stepper */}
                <Stepper activeStep={activeStep} orientation="vertical">
                  {/* Step 1: Select Wallet */}
                  <Step>
                    <StepLabel>
                      <Typography variant="body1" fontWeight={600}>
                        {t('SelectPaymentWallet') || 'Select Payment Wallet'}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {walletsLoading ? (
                        <Box display="flex" justifyContent="center" py={2}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : planWallets.length === 0 ? (
                        <Alert severity="warning" sx={{ borderRadius: 2 }}>
                          {t('NoWalletsForPlan') || 'No payment wallets available for this plan. Please contact support.'}
                        </Alert>
                      ) : (
                        <RadioGroup
                          value={String(getField(selectedWallet, 'WalletId', 'walletId', 'Id', 'id') || '')}
                          onChange={(e) => {
                            const wallet = planWallets.find(w => {
                              const wId = getField(w, 'WalletId', 'walletId', 'Id', 'id');
                              return String(wId) === e.target.value;
                            });
                            setSelectedWallet(wallet || null);
                          }}
                        >
                          {planWallets.map((wallet, wIndex) => {
                            const wId = getField(wallet, 'WalletId', 'walletId', 'Id', 'id');
                            const wName = getField(wallet, 'WalletName', 'walletName', 'Wallet.Name') || 'Wallet';
                            const wNumber = getField(wallet, 'WalletNumber', 'walletNumber') || '';
                            const wAccountName = getField(wallet, 'WalletAccountName', 'walletAccountName') || '';
                            const wImage = getField(wallet, 'WalletImage', 'walletImage', 'Wallet.Image', 'Wallet.ImageUrl');
                            const selectedWId = getField(selectedWallet, 'WalletId', 'walletId', 'Id', 'id');
                            const isSelected = selectedWId && String(selectedWId) === String(wId);

                            return (
                              <Paper
                                key={wId || wIndex}
                                sx={{
                                  p: 1.5,
                                  mb: 1,
                                  borderRadius: 2,
                                  border: `1px solid ${alpha(isSelected ? '#ad1457' : '#000', isSelected ? 0.4 : 0.1)}`,
                                  bgcolor: isSelected ? alpha('#ad1457', 0.04) : 'transparent',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  '&:hover': {
                                    borderColor: alpha('#ad1457', 0.3),
                                    bgcolor: alpha('#ad1457', 0.02)
                                  }
                                }}
                                onClick={() => setSelectedWallet(wallet)}
                              >
                                <FormControlLabel
                                  value={String(wId)}
                                  control={<Radio size="small" sx={{ color: '#ad1457', '&.Mui-checked': { color: '#ad1457' } }} />}
                                  label={
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                      {wImage && (
                                        <Avatar src={wImage} sx={{ width: 32, height: 32 }} />
                                      )}
                                      <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                          {wName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {wNumber}{wAccountName ? ` - ${wAccountName}` : ''}
                                        </Typography>
                                      </Box>
                                    </Stack>
                                  }
                                  sx={{ m: 0, width: '100%' }}
                                />
                              </Paper>
                            );
                          })}
                        </RadioGroup>
                      )}
                      <Box sx={{ mt: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => setActiveStep(1)}
                          disabled={!selectedWallet}
                          sx={{ textTransform: 'none', bgcolor: '#ad1457', '&:hover': { bgcolor: '#880e4f' } }}
                        >
                          {t('Continue') || 'Continue'}
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 2: Upload Receipt */}
                  <Step>
                    <StepLabel>
                      <Typography variant="body1" fontWeight={600}>
                        {t('UploadPaymentReceipt') || 'Upload Payment Receipt'}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      {selectedWallet && (() => {
                        const swName = getField(selectedWallet, 'WalletName', 'walletName', 'Wallet.Name') || 'Wallet';
                        const swNumber = getField(selectedWallet, 'WalletNumber', 'walletNumber') || '';
                        const swAccountName = getField(selectedWallet, 'WalletAccountName', 'walletAccountName') || '';

                        return (
                          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                            <Typography variant="body2">
                              {t('TransferTo') || 'Please transfer'}{' '}
                              <strong>{displayPrice}</strong>{' '}
                              {t('To') || 'to'}{' '}
                              <strong>{swName}</strong> ({swNumber})
                              {swAccountName && ` - ${swAccountName}`}
                            </Typography>
                          </Alert>
                        );
                      })()}

                      <Box
                        onClick={() => fileInputRef.current?.click()}
                        sx={{
                          border: `2px dashed ${alpha(receiptPreview ? '#2e7d32' : '#000', 0.2)}`,
                          borderRadius: 2,
                          p: 3,
                          textAlign: 'center',
                          cursor: 'pointer',
                          bgcolor: receiptPreview ? alpha('#2e7d32', 0.03) : alpha('#000', 0.02),
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: alpha('#ad1457', 0.4),
                            bgcolor: alpha('#ad1457', 0.03)
                          }
                        }}
                      >
                        {receiptPreview ? (
                          <Box>
                            <img
                              src={receiptPreview}
                              alt="Receipt Preview"
                              style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain', borderRadius: 8 }}
                            />
                            <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                              {t('ReceiptUploaded') || 'Receipt uploaded - Click to change'}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <CloudUploadIcon sx={{ fontSize: 48, color: alpha('#000', 0.2), mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              {t('ClickToUploadReceipt') || 'Click to upload payment receipt'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('SupportedFormats') || 'Supported: JPG, PNG, WEBP (Max 10MB)'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleReceiptFileChange}
                        style={{ display: 'none' }}
                      />

                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" spacing={1}>
                          <Button
                            onClick={() => setActiveStep(0)}
                            sx={{ textTransform: 'none' }}
                          >
                            {t('Back') || 'Back'}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={() => setActiveStep(2)}
                            disabled={!receiptPreview}
                            sx={{ textTransform: 'none', bgcolor: '#ad1457', '&:hover': { bgcolor: '#880e4f' } }}
                          >
                            {t('Continue') || 'Continue'}
                          </Button>
                        </Stack>
                      </Box>
                    </StepContent>
                  </Step>

                  {/* Step 3: Confirm */}
                  <Step>
                    <StepLabel>
                      <Typography variant="body1" fontWeight={600}>
                        {t('ConfirmPurchase') || 'Confirm Purchase'}
                      </Typography>
                    </StepLabel>
                    <StepContent>
                      <Paper sx={{ p: 2, bgcolor: alpha('#000', 0.02), borderRadius: 2, mb: 2 }}>
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('Plan') || 'Plan'}</Typography>
                            <Typography variant="body2" fontWeight={600}>{spName}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('Designs') || 'Designs'}</Typography>
                            <Typography variant="body2" fontWeight={600}>{spDesigns}</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('Amount') || 'Amount'}</Typography>
                            <Typography variant="body2" fontWeight={700} color="#ad1457">
                              {displayPrice}
                            </Typography>
                          </Grid>
                                          <Grid item xs={6}>
                            <Typography variant="caption" color="text.secondary">{t('PaymentWallet') || 'Payment Wallet'}</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {getField(selectedWallet, 'WalletName', 'walletName', 'Wallet.Name') || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getField(selectedWallet, 'WalletNumber', 'walletNumber') || ''}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>

                      <TextField
                        fullWidth
                        size="small"
                        label={t('Notes') || 'Notes (Optional)'}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        placeholder={t('NotesPlaceholder') || 'Any additional notes...'}
                        sx={{ mb: 2 }}
                      />

                      {/* Receipt preview in confirmation */}
                      {receiptPreview && (
                        <Paper sx={{ p: 1.5, mb: 2, bgcolor: alpha('#2e7d32', 0.03), borderRadius: 2, border: `1px solid ${alpha('#2e7d32', 0.15)}` }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <img
                              src={receiptPreview}
                              alt="Receipt"
                              style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                            />
                            <Box>
                              <Typography variant="caption" color="success.main" fontWeight={600}>
                                <CheckCircleIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                {t('ReceiptReady') || 'Receipt ready to submit'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {receiptFile?.name || 'receipt.jpg'}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      )}

                      <Stack direction="row" spacing={1}>
                        <Button
                          onClick={() => setActiveStep(1)}
                          disabled={purchasing}
                          sx={{ textTransform: 'none' }}
                        >
                          {t('Back') || 'Back'}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handlePurchase}
                          disabled={purchasing}
                          startIcon={purchasing ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                          sx={{
                            textTransform: 'none',
                            bgcolor: '#2e7d32',
                            fontWeight: 700,
                            '&:hover': { bgcolor: '#1b5e20' }
                          }}
                        >
                          {purchasing
                            ? (t('Processing') || 'Processing...')
                            : (t('ConfirmAndPurchase') || 'Confirm & Purchase')
                          }
                        </Button>
                      </Stack>
                    </StepContent>
                  </Step>
                </Stepper>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </Box>
  );
}